import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { 
  MapPin, 
  Phone, 
  User, 
  Navigation, 
  CheckCircle, 
  XCircle,
  Package,
  Bike,
  AlertCircle,
  ChevronRight
} from 'lucide-react';
import { toast } from '../../hooks/use-toast';
import BikerLayout from '../../components/biker/BikerLayout';
import DriverNavigationView from '../../components/driver/DriverNavigationView';

const BikerActiveRide = () => {
  const navigate = useNavigate();
  const [activeDeliveries, setActiveDeliveries] = useState([]);
  const [selectedDelivery, setSelectedDelivery] = useState(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [showFullNavigation, setShowFullNavigation] = useState(false);

  useEffect(() => {
    fetchActiveDeliveries();
  }, []);

  const fetchActiveDeliveries = async () => {
    try {
      const api = (await import('../../utils/api')).default;
      const response = await api.get('/bikers/my-deliveries');
      
      // Get all accepted or in_progress deliveries
      const activeRides = response.data.rides.filter(
        ride => ride.status === 'accepted' || ride.status === 'in_progress' || ride.status === 'arriving'
      );
      
      setActiveDeliveries(activeRides);
      // Auto-select first delivery if none selected
      if (activeRides.length > 0 && !selectedDelivery) {
        setSelectedDelivery(activeRides[0]);
      } else if (selectedDelivery) {
        // Update selected delivery with fresh data
        const updated = activeRides.find(r => r.id === selectedDelivery.id);
        setSelectedDelivery(updated || activeRides[0] || null);
      }
      setLoading(false);
    } catch (error) {
      console.error('Error fetching active deliveries:', error);
      toast({
        title: 'Error',
        description: 'Failed to load active deliveries',
        variant: 'destructive',
      });
      setLoading(false);
    }
  };

  const handleRideAction = async (action) => {
    if (!selectedDelivery) return;

    setActionLoading(true);
    try {
      const api = (await import('../../utils/api')).default;
      await api.post('/bikers/delivery-action', {
        rideId: selectedDelivery.id,
        action: action
      });

      toast({
        title: 'Success',
        description: `Delivery ${action === 'start' ? 'started' : action === 'finish' ? 'completed' : 'updated'}`,
      });

      if (action === 'finish') {
        // If there are more deliveries, stay on page and refresh
        if (activeDeliveries.length > 1) {
          await fetchActiveDeliveries();
        } else {
          navigate('/biker/history');
        }
      } else {
        fetchActiveDeliveries();
      }
    } catch (error) {
      console.error(`Error ${action} delivery:`, error);
      toast({
        title: 'Error',
        description: error.response?.data?.detail || `Failed to ${action} delivery`,
        variant: 'destructive',
      });
    } finally {
      setActionLoading(false);
    }
  };

  const handleCancel = async () => {
    if (!selectedDelivery) return;
    
    if (!window.confirm('Are you sure you want to cancel this delivery?')) {
      return;
    }

    setActionLoading(true);
    try {
      const api = (await import('../../utils/api')).default;
      await api.post('/bikers/delivery-action', {
        rideId: selectedDelivery.id,
        action: 'cancel'
      });

      toast({
        title: 'Delivery Cancelled',
        description: 'The delivery has been cancelled',
      });

      // Refresh the list
      await fetchActiveDeliveries();
      if (activeDeliveries.length <= 1) {
        navigate('/biker/dashboard');
      }
    } catch (error) {
      console.error('Error cancelling delivery:', error);
      toast({
        title: 'Error',
        description: error.response?.data?.detail || 'Failed to cancel ride',
        variant: 'destructive',
      });
    } finally {
      setActionLoading(false);
    }
  };

  const openNavigation = (delivery = selectedDelivery) => {
    if (!delivery) return;
    setSelectedDelivery(delivery);
    setShowFullNavigation(true);
  };

  if (loading) {
    return (
      <BikerLayout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-600"></div>
        </div>
      </BikerLayout>
    );
  }

  if (activeDeliveries.length === 0) {
    return (
      <BikerLayout>
        <Card className="p-12 text-center">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-orange-100 rounded-full mb-4">
            <Bike className="h-8 w-8 text-orange-600" />
          </div>
          <h2 className="text-xl font-semibold text-gray-900 mb-2">No Active Deliveries</h2>
          <p className="text-gray-600 mb-6">
            You don't have any active deliveries at the moment.
          </p>
          <div className="flex gap-3 justify-center">
            <Button onClick={() => navigate('/biker/deliveries')} variant="outline">
              View Available Deliveries
            </Button>
            <Button onClick={() => navigate('/biker/rides')} className="bg-orange-600 hover:bg-orange-700">
              View Rides
            </Button>
          </div>
        </Card>
      </BikerLayout>
    );
  }

  const isDelivery = selectedDelivery?.type === 'delivery';
  const statusColors = {
    'accepted': 'bg-blue-100 text-blue-800',
    'arriving': 'bg-purple-100 text-purple-800',
    'in_progress': 'bg-green-100 text-green-800',
  };

  return (
    <BikerLayout>
      <div className="space-y-6">
        {/* Header with count */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">
              Active Deliveries
              {activeDeliveries.length > 1 && (
                <Badge className="ml-3 bg-orange-500 text-white">
                  {activeDeliveries.length} Active
                </Badge>
              )}
            </h1>
            <p className="text-gray-600 mt-1">
              {activeDeliveries.length > 1 
                ? `Managing ${activeDeliveries.length} deliveries` 
                : 'Manage your current delivery'}
            </p>
          </div>
          {selectedDelivery && (
            <Badge className={statusColors[selectedDelivery.status] || 'bg-gray-100'}>
          {selectedDelivery.status.replace('_', ' ').toUpperCase()}
            </Badge>
          )}
        </div>

        {/* Multiple Deliveries List */}
        {activeDeliveries.length > 1 && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
            {activeDeliveries.map((delivery) => (
              <Card 
                key={delivery.id}
                className={`p-4 cursor-pointer transition-all ${
                  selectedDelivery?.id === delivery.id 
                    ? 'ring-2 ring-orange-500 bg-orange-50' 
                    : 'hover:shadow-md'
                }`}
                onClick={() => setSelectedDelivery(delivery)}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Package className="h-4 w-4 text-orange-600" />
                    <span className="font-medium text-sm truncate max-w-[120px]">
                      {delivery.dropoff?.split(',')[0] || 'Delivery'}
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge className={`text-xs ${statusColors[delivery.status]}`}>
                      {delivery.status === 'in_progress' ? 'In Progress' : delivery.status}
                    </Badge>
                    <ChevronRight className="h-4 w-4 text-gray-400" />
                  </div>
                </div>
                <p className="text-xs text-gray-500 mt-1 truncate">K{delivery.price}</p>
              </Card>
            ))}
          </div>
        )}

        {/* Selected Delivery Details */}
        {selectedDelivery && (
        <Card className="p-6">
          <div className="flex items-start justify-between mb-6">
            <div className="flex items-center gap-3">
              <div className="p-3 bg-orange-100 rounded-lg">
                {isDelivery ? (
                  <Package className="h-6 w-6 text-orange-600" />
                ) : (
                  <Bike className="h-6 w-6 text-orange-600" />
                )}
              </div>
              <div>
                <h3 className="font-semibold text-lg">
                  {isDelivery ? 'Package Delivery' : 'Motorbike Ride'}
                </h3>
                <p className="text-sm text-gray-600">ID: {selectedDelivery.id.slice(0, 8)}</p>
              </div>
            </div>
            <div className="text-right">
              <p className="text-2xl font-bold text-orange-600">K{selectedDelivery.price}</p>
              <p className="text-sm text-gray-600">{selectedDelivery.distance?.toFixed(1)} km</p>
            </div>
          </div>

          <div className="space-y-4">
            <div className="p-4 bg-gray-50 rounded-lg">
              <div className="flex items-start gap-3 mb-3">
                <MapPin className="h-5 w-5 text-gray-400 mt-0.5" />
                <div className="flex-1">
                  <p className="text-sm font-medium text-gray-700">Pickup Location</p>
                  <p className="text-gray-900">{selectedDelivery.pickup}</p>
                </div>
              </div>

              <div className="flex items-start gap-3">
                <MapPin className="h-5 w-5 text-orange-600 mt-0.5" />
                <div className="flex-1">
                  <p className="text-sm font-medium text-gray-700">Dropoff Location</p>
                  <p className="text-gray-900">{selectedDelivery.dropoff}</p>
                </div>
              </div>
            </div>

            {(selectedDelivery.userName || selectedDelivery.userPhone) && (
              <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
                <h4 className="font-medium text-blue-900 mb-2">Customer Information</h4>
                <div className="space-y-2">
                  {selectedDelivery.userName && (
                    <div className="flex items-center gap-2">
                      <User className="h-4 w-4 text-blue-600" />
                      <span className="text-blue-900">{selectedDelivery.userName}</span>
                    </div>
                  )}
                  {selectedDelivery.userPhone && (
                    <div className="flex items-center gap-2">
                      <Phone className="h-4 w-4 text-blue-600" />
                      <a href={`tel:${selectedDelivery.userPhone}`} className="text-blue-600 hover:underline">
                        {selectedDelivery.userPhone}
                      </a>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>

          <div className="mt-6 space-y-3">
            <Button
              onClick={() => openNavigation(selectedDelivery)}
              className="w-full bg-blue-600 hover:bg-blue-700"
            >
              <Navigation className="h-5 w-5 mr-2" />
              Open Navigation
            </Button>

            {selectedDelivery.status === 'accepted' && (
              <Button
                onClick={() => handleRideAction('start')}
                disabled={actionLoading}
                className="w-full bg-green-600 hover:bg-green-700"
              >
                <CheckCircle className="h-5 w-5 mr-2" />
                {actionLoading ? 'Starting...' : 'Start Trip'}
              </Button>
            )}

            {selectedDelivery.status === 'in_progress' && (
              <Button
                onClick={() => handleRideAction('finish')}
                disabled={actionLoading}
                className="w-full bg-orange-600 hover:bg-orange-700"
              >
                <CheckCircle className="h-5 w-5 mr-2" />
                {actionLoading ? 'Completing...' : 'Complete Trip'}
              </Button>
            )}

            <Button
              onClick={handleCancel}
              disabled={actionLoading}
              variant="outline"
              className="w-full text-red-600 border-red-300 hover:bg-red-50"
            >
              <XCircle className="h-5 w-5 mr-2" />
              Cancel {isDelivery ? 'Delivery' : 'Ride'}
            </Button>
          </div>
        </Card>
        )}

        <Card className="p-4 bg-orange-50 border-orange-200">
          <div className="flex gap-3">
            <AlertCircle className="h-5 w-5 text-orange-600 flex-shrink-0" />
            <div>
              <p className="text-sm font-medium text-orange-900">
                {activeDeliveries.length > 1 ? 'Multiple Deliveries Active' : 'Safety Reminder'}
              </p>
              <p className="text-sm text-orange-700 mt-1">
                  ? `You have ${activeDeliveries.length} active deliveries. Click on each card above to manage them individually.`
                  : 'Always wear your helmet, follow traffic rules, and confirm the customer&apos;s identity before starting the trip.'}
              </p>
            </div>
          </div>
        </Card>
      </div>

      {/* Full-screen Navigation View */}
      {showFullNavigation && selectedDelivery && (
        <DriverNavigationView
          ride={{
            ...selectedDelivery,
            pickup: selectedDelivery.pickup,
            dropoff: selectedDelivery.dropoff,
            pickupCoords: selectedDelivery.pickupCoords || selectedDelivery.pickupLocation,
            dropoffCoords: selectedDelivery.dropoffCoords || selectedDelivery.dropoffLocation,
            userName: selectedDelivery.senderName || selectedDelivery.userName,
            userPhone: selectedDelivery.senderPhone || selectedDelivery.userPhone,
            status: selectedDelivery.status === 'accepted' ? 'accepted' : 'in_progress'
          }}
          driverLocation={null}
          onStatusUpdate={async (action) => {
            await handleRideAction(action);
            if (action === 'complete') {
              setShowFullNavigation(false);
            }
          }}
          onCall={() => {
            const phone = selectedDelivery.senderPhone || selectedDelivery.userPhone;
            if (phone) window.location.href = `tel:${phone}`;
          }}
          onClose={() => setShowFullNavigation(false)}
          isUpdating={actionLoading}
        />
      )}
    </BikerLayout>
  );
};

export default BikerActiveRide;
